
import React, { useState, useEffect, useLayoutEffect } from 'react';

export interface TourStep {
    targetId: string;
    title: string;
    content: string;
    position?: 'top' | 'bottom' | 'left' | 'right';
}

interface TourGuideProps {
    steps: TourStep[];
    isOpen: boolean;
    onComplete: () => void;
    onSkip: () => void;
}

const TourGuide: React.FC<TourGuideProps> = ({ steps, isOpen, onComplete, onSkip }) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [rect, setRect] = useState<DOMRect | null>(null);

    // Reset step when opened
    useEffect(() => {
        if (isOpen) setCurrentStep(0);
    }, [isOpen]);

    const updateRect = () => {
        const step = steps[currentStep];
        if (step) {
            const el = document.getElementById(step.targetId);
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
                setRect(el.getBoundingClientRect());
            } else {
                setRect(null);
            }
        }
    };

    useLayoutEffect(() => {
        if (!isOpen) return;
        updateRect();
        
        // Handle resize and scroll
        const handleResize = () => updateRect();
        window.addEventListener('resize', handleResize);
        window.addEventListener('scroll', handleResize, true);
        
        // Polling for layout shifts (e.g. initial render or animations)
        const interval = setInterval(updateRect, 200);

        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('scroll', handleResize, true);
            clearInterval(interval);
        };
    }, [currentStep, isOpen]);

    const handleNext = () => {
        if (currentStep < steps.length - 1) {
            setCurrentStep(prev => prev + 1);
        } else {
            onComplete();
        }
    };

    if (!isOpen || !rect) return null;

    const step = steps[currentStep];

    // Determine tooltip position logic (simplified)
    const isBottom = rect.bottom + 200 < window.innerHeight;
    const tooltipTop = isBottom ? rect.bottom + 20 : Math.max(20, rect.top - 200);
    const tooltipLeft = Math.max(16, Math.min(window.innerWidth - 316, rect.left + (rect.width / 2) - 150));

    return (
        <div className="fixed inset-0 z-[200] overflow-hidden">
            {/* Spotlight Effect using giant shadow */}
            <div 
                className="absolute transition-all duration-300 ease-in-out shadow-[0_0_0_9999px_rgba(0,0,0,0.75)] rounded-xl border-2 border-apple-gold"
                style={{
                    top: rect.top - 4,
                    left: rect.left - 4,
                    width: rect.width + 8,
                    height: rect.height + 8,
                }}
            >
                {/* Ping animation to draw attention */}
                <div className="absolute inset-0 rounded-xl animate-ping border border-apple-gold/50 opacity-30 pointer-events-none"></div>
            </div>

            {/* Tooltip Card */}
            <div 
                className="absolute w-[300px] bg-white dark:bg-[#1d1d1f] p-6 rounded-3xl shadow-2xl border border-black/5 dark:border-white/10 transition-all duration-300 animate-in fade-in slide-in-from-bottom-2"
                style={{
                    top: tooltipTop,
                    left: tooltipLeft
                }}
            >
                <div className="flex justify-between items-center mb-3">
                    <h3 className="font-bold text-lg text-slate-900 dark:text-white">{step.title}</h3>
                    <div className="px-2 py-1 rounded-full bg-slate-100 dark:bg-white/10 text-[10px] font-bold text-slate-500">
                        {currentStep + 1} / {steps.length}
                    </div>
                </div>
                <p className="text-sm text-slate-500 dark:text-slate-300 mb-6 leading-relaxed">
                    {step.content}
                </p>
                <div className="flex justify-between items-center">
                    <button onClick={onSkip} className="text-xs font-bold text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors">
                        Skip
                    </button>
                    <button 
                        onClick={handleNext}
                        className="px-6 py-2 bg-apple-gold text-black text-xs font-bold rounded-full hover:scale-105 transition-transform shadow-lg shadow-apple-gold/20"
                    >
                        {currentStep === steps.length - 1 ? "Finish" : "Next"}
                    </button>
                </div>
            </div>
        </div>
    );
};
export default TourGuide;
